import React from "react";
import { Map } from "immutable";

import {
  checkSelectionForButtonElements,
  checkSelectionForFormFieldConversion,
} from "@/utils/PropertyUtils";
import IDUtils from "@/utils/IDUtils";
import ImageToolbarButtons from "./ToolbarButtonSets/ImageToolbarButtons";
import PotentialButtonToolbarButtons from "./ToolbarButtonSets/PotentialButtonToolbarButtons";
import PotentialFormFieldToolbarButtons from "./ToolbarButtonSets/PotentialFormFieldToolbarButtons";
import GroupToolbarButtons from "./ToolbarButtonSets/GroupToolbarButtons";
import {
  canSelectedGroupDissolve,
  canRemoveFromGroup,
  canSelectionGroup,
  canSelectionJoinGroup,
} from "@/utils/GroupUtils";
import DesignViewStore from "@/stores/DesignViewStore";
import useStoreState from "../providers/useStoreState";
import useV2Feature from "@/hooks/useV2Feature";
import { FORMS_FLAG } from "@/constants/FeatureFlags.mjs";

import { GRAPHIC } from "@/constants/DataTypes";
import { CROP_MODE, GROUP_FOCUS_MODE } from "@/constants/UIConstants";

import styles from "./ElementToolbar.module.css";
import BlockUtils from "@/utils/BlockUtils";

const POTENTIAL_BUTTON_ACTIONS = "potentialButton";
const POTENTIAL_FORM_FIELD_ACTIONS = "potentialFormField";
const IMAGE_ACTIONS = "image";
const GROUP_ACTIONS = "group";

// mostly copied from other branch
const ACTIONS_BUTTONS_MAP = {
  [POTENTIAL_BUTTON_ACTIONS]: PotentialButtonToolbarButtons,
  [POTENTIAL_FORM_FIELD_ACTIONS]: PotentialFormFieldToolbarButtons,
  [IMAGE_ACTIONS]: ImageToolbarButtons,
  [GROUP_ACTIONS]: GroupToolbarButtons,
};

const collectElementData = (selectedElements, blockContainer) => {
  const block = blockContainer.get("selectedBlock");

  return selectedElements.map((elemInfo) => {
    return block.getIn(IDUtils.getArray(elemInfo.id, 1));
  });
};

export const buttonSet = (block, selectedElements, mode, layout, isFormsEnabled = false) => {
  const sets = [];

  if (checkSelectionForButtonElements(selectedElements, block)) {
    sets.push(POTENTIAL_BUTTON_ACTIONS);
  }

  if (isFormsEnabled && checkSelectionForFormFieldConversion(selectedElements, block)) {
    sets.push(POTENTIAL_FORM_FIELD_ACTIONS);
  }

  if (selectedElements.size === 1 && selectedElements.first().type === GRAPHIC) {
    sets.push(IMAGE_ACTIONS);
  }

  let visibleSelectedElements = selectedElements.filter((elemInfo) => {
    const elemData = block.getIn(IDUtils.getArray(elemInfo.id, 1));
    return BlockUtils.isVisibleInLayout(elemData, layout);
  });

  const containerPath = selectedElements.first()?.containedBy;

  // account for hidden group and element visibility for toolbar position.
  if (containerPath) {
    const container = block.getIn(IDUtils.getArray(containerPath, 1));
    const containerIsVisible = BlockUtils.isVisibleInLayout(container, layout);

    if (!containerIsVisible) visibleSelectedElements = Map();
  }

  const canRemove = canRemoveFromGroup(visibleSelectedElements);
  const canDissolve = canSelectedGroupDissolve(visibleSelectedElements);

  const canGroup = canSelectionGroup(visibleSelectedElements);
  const canJoinGroup = canSelectionJoinGroup(visibleSelectedElements);

  if ((canRemove || canDissolve || canGroup || canJoinGroup) && mode !== CROP_MODE) {
    sets.push(GROUP_ACTIONS);
  }

  return sets;
};

const ElementToolbar = ({ blockContainer, layout }) => {
  const selectedElements = blockContainer.getIn(["selectedBlock", "selected", "elements"]);
  const mode = useStoreState(DesignViewStore, DesignViewStore.getModeType);
  const isFormsEnabled = useV2Feature(FORMS_FLAG);

  if (!selectedElements) return null;

  const actionSets = buttonSet(
    blockContainer.get("selectedBlock"),
    selectedElements,
    mode,
    layout,
    isFormsEnabled,
  );
  const selectedElementsData = collectElementData(selectedElements, blockContainer);
  const isSingleElementEdit = selectedElements.size === 1 && selectedElements.first().edit;
  const isLocked = selectedElementsData.some((element) => element.get("lockEditing"));
  if (!actionSets.length || (mode === GROUP_FOCUS_MODE && isSingleElementEdit)) return null;

  return (
    <div className={styles.toolbar}>
      {actionSets.map((actionSet) => {
        const ActionsComponent = ACTIONS_BUTTONS_MAP[actionSet];
        return (
          <ActionsComponent
            key={actionSet}
            blockContainer={blockContainer}
            selectedElementsData={selectedElementsData}
            selectedElements={selectedElements}
            selectedBlock={blockContainer.get("selectedBlock")}
            mode={mode}
            isLocked={isLocked}
            layout={layout}
          />
        );
      })}
    </div>
  );
};

export default ElementToolbar;
